import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe } from '@angular/common';

export interface PeriodHistoryEntry {
  startTimestamp: number;
  endTimestamp: number;
}

@Component({
  selector: 'app-period-history',
  templateUrl: './period-history.component.html',
  styleUrls: ['./period-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PeriodHistoryComponent implements OnChanges {
  @Input() periods: PeriodHistoryEntry[] = [];
  @Output() periodSelected = new EventEmitter<PeriodHistoryEntry>();
  @Output() periodRemoved = new EventEmitter<PeriodHistoryEntry>();

  convertedHistoryPeriods: {
    startLabel: string;
    endLabel: string;
    startTimestamp: number;
    endTimestamp: number;
  }[] = [];
  historyExpanded = false;
  readonly historyCollapsedCount = 3;

  constructor(private datePipe: DatePipe) {}

  get visibleHistoryPeriods() {
    return this.historyExpanded
      ? this.convertedHistoryPeriods
      : this.convertedHistoryPeriods.slice(0, this.historyCollapsedCount);
  }

  get hiddenHistoryCount(): number {
    return Math.max(
      0,
      this.convertedHistoryPeriods.length - this.historyCollapsedCount,
    );
  }

  ngOnChanges() {
    this.convertPeriods();
    if (this.convertedHistoryPeriods.length <= this.historyCollapsedCount) {
      this.historyExpanded = false;
    }
  }

  toggleHistoryExpanded(): void {
    this.historyExpanded = !this.historyExpanded;
  }

  selectPeriod(period: PeriodHistoryEntry): void {
    this.periodSelected.emit({
      startTimestamp: period.startTimestamp,
      endTimestamp: period.endTimestamp,
    });
  }

  removePeriod(event: MouseEvent, period: PeriodHistoryEntry): void {
    event.stopPropagation();
    this.periodRemoved.emit({
      startTimestamp: period.startTimestamp,
      endTimestamp: period.endTimestamp,
    });
    if (this.convertedHistoryPeriods.length - 1 <= this.historyCollapsedCount) {
      this.historyExpanded = false;
    }
  }

  trackByPeriod = (_: number, period: PeriodHistoryEntry): string =>
    `${period.startTimestamp}__${period.endTimestamp}`;

  private convertPeriods(): void {
    if (!this.periods || this.periods.length === 0) {
      this.convertedHistoryPeriods = [];
      return;
    }

    this.convertedHistoryPeriods = [];

    this.periods.forEach((period) => {
      const startLabel = this.datePipe.transform(
        new Date(period.startTimestamp * 1000),
        'dd MMM yyyy, HH:mm',
      );
      const endLabel = this.datePipe.transform(
        new Date(period.endTimestamp * 1000),
        'dd MMM yyyy, HH:mm',
      );

      const index = this.convertedHistoryPeriods.findIndex(
        (i) =>
          i.startTimestamp === period.startTimestamp &&
          i.endTimestamp === period.endTimestamp,
      );

      if (index !== -1) {
        const [item] = this.convertedHistoryPeriods.splice(index, 1);
        this.convertedHistoryPeriods.push(item);
      } else {
        this.convertedHistoryPeriods.push({
          startLabel,
          endLabel,
          startTimestamp: period.startTimestamp,
          endTimestamp: period.endTimestamp,
        });
      }
    });

    this.convertedHistoryPeriods.reverse();
  }
}
